import React, { useEffect, useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { PendingSharedSession, acceptSharedGameSession, getPendingSharedSessions } from '../lib/game/sessionService';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface PendingSharedSessionsProps {
  userId: string;
}

export default function PendingSharedSessions({ userId }: PendingSharedSessionsProps) {
  const [pendingSessions, setPendingSessions] = useState<PendingSharedSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  useEffect(() => {
    loadPendingSessions();
  }, [userId]);

  const loadPendingSessions = async () => {
    if (!userId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const sessions = await getPendingSharedSessions(userId);
      setPendingSessions(sessions);
    } catch (err) {
      console.error('Erro ao carregar sessões pendentes:', err);
      setError('Não foi possível carregar as sessões pendentes.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = async (shareId: string) => {
    setProcessingIds(prev => new Set([...prev, shareId]));
    
    try {
      await acceptSharedGameSession(shareId);
      
      // Remova a sessão da lista depois de aceita
      setPendingSessions(prev => prev.filter(session => session.id !== shareId));
      
      toast({
        title: "Sessão aceita",
        description: "A sessão de jogo foi adicionada ao seu histórico.",
        variant: "default",
      });
    } catch (err) {
      console.error('Erro ao aceitar sessão:', err);
      toast({
        title: "Erro",
        description: "Não foi possível aceitar a sessão de jogo.",
        variant: "destructive",
      });
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(shareId);
        return newSet;
      });
    }
  };

  const handleReject = async (shareId: string) => {
    // TODO: Implementar rejectSharedGameSession no sessionService e a RPC no Supabase
    // const rejectSharedGameSession = (await import('../lib/game/sessionService')).rejectSharedGameSession;
    setProcessingIds(prev => new Set([...prev, shareId]));
    
    try {
       // Chamada da função (quando implementada)
      // await rejectSharedGameSession(shareId);
      
      // Por enquanto, remove da lista local e mostra toast (comportamento atual)
      setPendingSessions(prev => prev.filter(session => session.id !== shareId));
      toast({
        title: "Sessão rejeitada",
        description: "A sessão de jogo foi rejeitada (ação pendente no backend).",
        variant: "default",
      });
      
    } catch (err) {
       console.error('Erro ao rejeitar sessão:', err);
       toast({
         title: "Erro",
         description: "Não foi possível rejeitar a sessão de jogo.",
         variant: "destructive",
       });
    } finally {
       setProcessingIds(prev => {
         const newSet = new Set(prev);
         newSet.delete(shareId);
         return newSet;
       });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-500">
        <p>{error}</p>
        <Button 
          onClick={loadPendingSessions} 
          variant="outline" 
          className="mt-2"
        >
          Tentar novamente
        </Button>
      </div>
    );
  }

  if (pendingSessions.length === 0) {
    return (
      <div className="text-center p-4 text-muted-foreground">
        <p>Nenhuma sessão de jogo compartilhada pendente.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {pendingSessions.map((session) => (
        <Card key={session.id} className="overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex justify-between items-start">
              <span className="truncate">{session.game_name}</span>
              <span className="text-xs text-muted-foreground flex items-center ml-2">
                <Clock size={12} className="mr-1" />
                {formatDistanceToNow(new Date(session.played_at), { 
                  addSuffix: true, 
                  locale: ptBR 
                })}
              </span>
            </CardTitle>
          </CardHeader>
          
          <CardContent className="pb-3">
            <div className="flex items-center my-2">
              <Avatar className="h-8 w-8 mr-2">
                {session.game_image ? (
                  <AvatarImage src={session.game_image} alt={session.game_name} />
                ) : null}
                <AvatarFallback>{session.game_name.substring(0, 2)}</AvatarFallback>
              </Avatar>
              
              <div className="text-sm">
                <p>Compartilhado por: <span className="font-medium">{session.owner_name}</span></p>
                <p className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(session.created_at), { 
                    addSuffix: true, 
                    locale: ptBR 
                  })}
                </p>
              </div>
            </div>
          </CardContent>
          
          <CardFooter className="flex justify-between pt-0">
            <Button
              variant="outline"
              size="sm"
              className="text-red-500 hover:text-red-700 hover:bg-red-100"
              onClick={() => handleReject(session.id)}
              disabled={processingIds.has(session.id)}
            >
              <XCircle className="h-4 w-4 mr-1" />
              Rejeitar
            </Button>
            
            <Button
              variant="default"
              size="sm"
              onClick={() => handleAccept(session.id)}
              disabled={processingIds.has(session.id)}
            >
              {processingIds.has(session.id) ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Aceitar
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
} 